import { Container, Stack } from 'component-library';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { PlotGroupGroupByEnum } from '@/api/rest/resources/types/plotGroup';
import { useActivePlotGroup, usePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { SecondaryNavProps } from '@/components/widgets/o/NavBar/NavBar';
import { useUIStyle } from '@/hooks/useUIStyle';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';

import { CreatePlotGroup } from '../../components/plot-group/CreatePlotGroup';
import { useIsLimitedAccessUser } from '../../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../../hooks/useOrganizationId';
import { PlotList, PlotListDesktopProps } from '../../plots/components/PlotList';

export const LandPageLayout = ({
  children,
  navBarProps,
  plotListProps,
}: {
  children: ReactNode;
  navBarProps?: Partial<SecondaryNavProps>;
  plotListProps?: Partial<PlotListDesktopProps>;
}) => {
  const { t } = useTranslation();

  const menuItems = useMenuItems();
  const plotGroup = useActivePlotGroup();

  const selectedPlotLabelValue = plotGroup?.filters?.plot__labels__id?.at(0)?.value;
  const pageTitle = selectedPlotLabelValue ?? t('navbar.land');

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();
  const organizationId = useOrganizationId();
  const isOrganizationMember = useIsOrganizationMember();
  const showGroupByOptions = !useUIStyle(OrganizationUIStyle.CONSERVATION);
  const [selectedPlotIds, setSelectedPlotIds] = useState<string[]>([]);

  const activePlotGroup = usePlotGroup();
  const isFiltersSelected = activeFilters?.length > 0;
  const hasGroupByFilters = activeFilters?.some((filter) => Object.keys(PlotGroupGroupByEnum).includes(filter.key));

  useEffect(() => {
    if (!isFiltersSelected) {
      setSelectedPlotIds([]);
    }
  }, [isFiltersSelected]);
  return (
    <SignedInPageLayout
      items={menuItems}
      withCustomContentContainer
      navBarProps={{
        title: pageTitle,
        showFilters: true,
        ...navBarProps,
        children: (
          <Stack direction='row' className='justify-end gap-4'>
            {!isLimitedAccessUser && isOrganizationMember && showGroupByOptions ? (
              <Stack direction='row' className='justify-end gap-4'>
                <CreatePlotGroup
                  key='filtered-group'
                  organizationId={organizationId}
                  plotGroup={activePlotGroup}
                  plotIds={selectedPlotIds}
                  disabled={!isFiltersSelected || false}
                />
              </Stack>
            ) : null}
            {navBarProps?.children ?? null}
          </Stack>
        ),
      }}
    >
      <Container gutterWidth={{ sm: 4 }} className='gap-10'>
        {children}
        {hasGroupByFilters ? (
          <PlotList {...plotListProps} />
        ) : (
          <PlotList
            enableRowSelection={true}
            defaultRowSelected={isFiltersSelected}
            setSelectedPlotIds={setSelectedPlotIds}
            selectedPlotIds={selectedPlotIds}
            {...plotListProps}
          />
        )}
      </Container>
    </SignedInPageLayout>
  );
};
